/**
 * useApiMutation — Hook für POST/PUT/PATCH/DELETE mit unified Lifecycle.
 *
 * Liefert {mutate, loading, error, reset}. mutate(args) führt die
 * Mutation aus und gibt das Ergebnis als Promise zurück. Optional
 * `onSuccess` und `onError` Callbacks für Side-Effects (Toast, Refetch).
 *
 * Beispiel:
 *   const create = useApiMutation(
 *     (req: TTokenCreateRequest) => staffApi.tokens.create(req),
 *     {
 *       onSuccess: (token) => {
 *         showToast('success', 'Token erstellt')
 *         refetch()
 *       },
 *       onError: (e) => showToast('error', e.message),
 *     }
 *   )
 *   await create.mutate({ description: '...', expires_in_days: 30 })
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { ApiError } from '../api/client'

export interface UseApiMutationOptions<TData> {
  onSuccess?: (data: TData) => void | Promise<void>
  onError?: (error: ApiError | Error) => void | Promise<void>
}

export interface UseApiMutationResult<TArgs, TData> {
  mutate: (args: TArgs) => Promise<TData>
  loading: boolean
  error: ApiError | Error | null
  reset: () => void
}

export function useApiMutation<TArgs, TData>(
  fn: (args: TArgs) => Promise<TData>,
  options: UseApiMutationOptions<TData> = {}
): UseApiMutationResult<TArgs, TData> {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<ApiError | Error | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const mutate = useCallback(async (args: TArgs): Promise<TData> => {
    setLoading(true)
    setError(null)
    try {
      const result = await fn(args)
      if (mountedRef.current) {
        setLoading(false)
      }
      await options.onSuccess?.(result)
      return result
    } catch (e) {
      const err = e as ApiError | Error
      if (mountedRef.current) {
        setError(err)
        setLoading(false)
      }
      await options.onError?.(err)
      throw err
    }
    // intentionally exclude options from deps — caller should memoize
    // callbacks if needed; otherwise we re-create mutate every render
    // which breaks `await mutate(...)` patterns
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fn])

  const reset = useCallback(() => {
    setError(null)
    setLoading(false)
  }, [])

  return { mutate, loading, error, reset }
}
